import { appApiUrl } from 'config'
import {
  API_BORROWER_APPLY_DRAFT,
  API_BORROWER_APPLY_PROMPT_STREAM,
  API_BORROWER_APPLY_SCHEMA,
  API_BORROWER_APPLY_SESSION,
  API_BORROWER_APPLY_SUBMIT,
  API_BORROWER_APPLY_UPLOAD,
  API_BORROWER_ASSISTANT_DRAFT,
  API_BORROWER_ASSISTANT_PROMPT_STREAM,
  API_BORROWER_ASSISTANT_SCHEMA,
  API_BORROWER_ASSISTANT_SUBMIT,
  API_BORROWER_ASSISTANT_UPLOAD,
} from 'config/api.url.constants'
import { store } from 'reducers'
import Api from 'services/api'

import type { AssistantStep } from './ai'

/** One question the configured application asks, as the server resolved it. */
export interface ApplicationField {
  fieldOrigin: string
  fieldKey: string
  title: string
  kind: 'text' | 'number' | 'date' | 'select' | 'toggle' | 'address'
  required: boolean
  options?: Record<string, string>
  stores?: 'key' | 'title'
  visibleLogic?: any[][]
  step: number
  stepTitle: string
}

export interface ApplicationState {
  answered: Array<{
    title: string
    fieldOrigin: string
    fieldKey: string
    value: any
    display?: string
    format?: 'money'
  }>
  missing: Array<{ title: string; fieldOrigin: string; fieldKey: string }>
  optionalRemaining: Array<{ title: string; fieldOrigin: string; fieldKey: string }>
  complete: boolean
  loanNumber?: number
  status: string
}

export interface ApplicationUpload {
  fileKey: string
  name: string
  readable: boolean
  filedAgainstLoan?: boolean
  reason?: string
}

export interface ApplicationDraft {
  state: ApplicationState
  answers: Record<string, Record<string, any>>
  uploads: ApplicationUpload[]
  history: Array<{ role: 'user' | 'model'; content: string; at: number }>
}

export interface ApplicationTurn {
  data: string
  state: ApplicationState
  steps?: Array<string | AssistantStep>
}

/**
 * Where a draft is kept between visits.
 *
 * Only an applicant with no account needs this: once someone is signed in, their draft is found by
 * their e-mail server-side. It is a bearer secret for one application, so it is kept out of the URL
 * and out of anything shared.
 */
const DRAFT_TOKEN_KEY = 'borrowerApplicationDraftToken'

export const storedDraftToken = (): string => {
  try {
    return window.localStorage.getItem(DRAFT_TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export const storeDraftToken = (token: string) => {
  try {
    if (token) window.localStorage.setItem(DRAFT_TOKEN_KEY, token)
    else window.localStorage.removeItem(DRAFT_TOKEN_KEY)
  } catch {
    // Private browsing, or site data blocked. The conversation still works for this visit.
  }
}

export const startApplicationSession = (): Promise<{ draftToken: string; state: ApplicationState }> =>
  Api.post(API_BORROWER_APPLY_SESSION, {})

export const getApplicationSchema = (signedIn: boolean): Promise<{ fields: ApplicationField[] }> =>
  Api.get(signedIn ? API_BORROWER_ASSISTANT_SCHEMA : API_BORROWER_APPLY_SCHEMA)

export const getApplicationDraft = (signedIn: boolean, draftToken?: string): Promise<ApplicationDraft> =>
  signedIn ? Api.get(API_BORROWER_ASSISTANT_DRAFT) : Api.post(API_BORROWER_APPLY_DRAFT, { draftToken })

export const submitApplication = (
  signedIn: boolean,
  draftToken?: string,
): Promise<{ submitted: boolean; loanNumber?: number; missing?: string[]; message?: string; token?: string }> =>
  signedIn ? Api.post(API_BORROWER_ASSISTANT_SUBMIT, {}) : Api.post(API_BORROWER_APPLY_SUBMIT, { draftToken })

export const uploadApplicationFiles = (
  signedIn: boolean,
  files: File[],
  draftToken?: string,
): Promise<{ data: ApplicationUpload[] }> =>
  Api.uploadFiles(
    signedIn ? API_BORROWER_ASSISTANT_UPLOAD : API_BORROWER_APPLY_UPLOAD,
    signedIn ? {} : { draftToken: draftToken || '' },
    {},
    files,
  )

/**
 * One turn, streamed.
 *
 * Reads the same event shape the staff assistant streams (`status` with its step, `delta` as the
 * answer is written, then one `done`), so the two readers stay in step if either side changes.
 */
export const streamApplicationPrompt = async (
  body: { prompt: string; turnId?: string; draftToken?: string },
  signedIn: boolean,
  handlers: {
    onStatus?: (text: string) => void
    onStep?: (step: AssistantStep) => void
    onDelta?: (delta: { text?: string; reset?: boolean }) => void
    signal?: AbortSignal
  } = {},
): Promise<ApplicationTurn> => {
  const token = store.getState()?.auth?.token
  const url = signedIn ? API_BORROWER_ASSISTANT_PROMPT_STREAM : API_BORROWER_APPLY_PROMPT_STREAM
  const res = await fetch(`${appApiUrl}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(signedIn ? { Authorization: `Bearer ${token || ''}` } : {}),
    },
    body: JSON.stringify(body),
    signal: handlers.signal,
  })
  if (!res.ok || !res.body) throw new Error(`The assistant could not be reached (${res.status}).`)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: ApplicationTurn = { data: '', state: null as any }

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep = buffer.indexOf('\n\n')
    while (sep !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const dataLine = raw.split('\n').find((line) => line.startsWith('data:'))
      if (dataLine) {
        let event: any = null
        try {
          event = JSON.parse(dataLine.slice(5).trim())
        } catch {
          event = null // a malformed line is skipped, not fatal
        }
        if (event?.type === 'status') {
          handlers.onStatus?.(String(event.text || ''))
          if (event.step?.label) handlers.onStep?.(event.step as AssistantStep)
        } else if (event?.type === 'delta') handlers.onDelta?.({ text: event.text, reset: event.reset })
        else if (event?.type === 'done') result = event.data
        else if (event?.type === 'error') throw new Error(event.message || 'The assistant stopped unexpectedly.')
      }
      sep = buffer.indexOf('\n\n')
    }
  }
  return result
}
